import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import SignIn from "../components/SignIn";
import SignUp from "../components/SignUp";
import { 
  PlusCircle, 
  Clock, 
  Calendar, 
  HelpCircle, 
  Code2, 
  Target, 
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Star,
  Zap
} from "lucide-react";
import { Helmet } from "react-helmet";

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);

  const handleGetStarted = () => {
    if (user) {
      navigate("/algorithms");
    } else {
      setShowSignUp(true);
    }
  };

  const features = [
    {
      icon: <PlusCircle className="w-8 h-8" />,
      title: "Question Bank",
      description: "Build your personal collection of algorithms and data structures with detailed notes and solutions.",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Smart Reminders",
      description: "Schedule review sessions with spaced repetition. Never forget to practice important concepts.",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Daily Queue",
      description: "Get a curated daily list of questions based on your progress and learning goals.",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: <HelpCircle className="w-8 h-8" />,
      title: "Progressive Hints",
      description: "Stuck? Access hints gradually to maintain the challenge while getting the support you need.",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "Code Snippets",
      description: "Store and organize solution code with syntax highlighting. Reference them anytime.",
      color: "from-cyan-500 to-sky-600"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Track Progress",
      description: "Visualize your growth with stats on difficulty levels, time spent, and completion rates.",
      color: "from-rose-500 to-pink-600"
    }
  ];

  const stats = [
    { value: "500+", label: "Problems Practiced" },
    { value: "98%", label: "Success Rate" },
    { value: "50+", label: "Companies" },
    { value: "30d", label: "Streak" }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Software Engineer at Google",
      text: "AlgoBuddy helped me land my dream job. The spaced repetition system is a game-changer.",
      avatar: "SC"
    },
    {
      name: "Marcus Johnson",
      role: "Senior Developer at Meta",
      text: "The daily queue feature keeps me consistent. I've solved 300+ problems in 6 months.",
      avatar: "MJ"
    },
    {
      name: "Emily Rodriguez",
      role: "Backend Engineer at Stripe",
      text: "The hint system strikes the perfect balance between challenge and guidance.",
      avatar: "ER"
    }
  ];

  return (
    <>
      <Helmet>
        <title>AlgoBuddy - Master Your Technical Interviews</title>
        <meta
          name="description"
          content="Organize, track, and review your interview preparation with AlgoBuddy. Master algorithms and ace your technical interviews."
        />
        <link rel="canonical" href="https://algobuddy.com" />
      </Helmet>

      <div className="bg-gray-950 text-gray-100 min-h-screen">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-lg border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Code2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  AlgoBuddy
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowSignIn(true)}
                  className="text-gray-300 hover:text-white px-4 py-2 text-sm font-medium transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setShowSignUp(true)}
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 shadow-lg shadow-emerald-500/25"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-gray-950 to-gray-950"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
                             radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)`
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-900/50 border border-gray-800 mb-8">
                <Zap className="w-4 h-4 text-amber-400 mr-2" />
                <span className="text-sm text-gray-300">Trusted by 10,000+ developers</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-gray-100 to-gray-400 bg-clip-text text-transparent">
                  Master
                </span>
                <br />
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Technical Interviews
                </span>
              </h1>
              
              <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                Your personal interview prep companion. Track progress, 
                schedule reviews, and build confidence one problem at a time.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={handleGetStarted}
                  className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full text-white font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl shadow-emerald-500/30"
                >
                  <span className="flex items-center">
                    Start Practicing Free
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => navigate('#features')}
                  className="px-8 py-4 border border-gray-700 rounded-full text-gray-300 font-medium text-lg hover:bg-gray-900 transition-all duration-300 hover:border-gray-600"
                >
                  See Features
                </button>
              </div>

              {/* Stats Row */}
              <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-gray-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  Everything You Need
                </span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Built by engineers, for engineers. Every feature designed with interview prep in mind.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-2xl bg-gray-900 border border-gray-800 hover:border-gray-700 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 text-white shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Loved by Developers
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-gray-900 border border-gray-800"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-medium">
                      {testimonial.avatar}
                    </div>
                    <div className="ml-3">
                      <div className="text-white font-medium">{testimonial.name}</div>
                      <div className="text-gray-500 text-sm">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-950 to-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Ace Your Next Interview?
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Join thousands of developers who have improved their interview skills with AlgoBuddy.
            </p>
            <button
              onClick={handleGetStarted}
              className="px-10 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full text-white font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl shadow-emerald-500/30"
            >
              Get Started for Free
            </button>
            <div className="mt-6 flex items-center justify-center text-gray-500 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2" />
              No credit card required • Start in 30 seconds
            </div>
          </div>
        </section>

        {/* Auth Modals */}
        {showSignIn && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md">
              <SignIn onClose={() => setShowSignIn(false)} />
            </div>
          </div>
        )}

        {showSignUp && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md">
              <SignUp onClose={() => setShowSignUp(false)} />
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="border-t border-gray-800 bg-gray-950 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-2 mb-4 md:mb-0">
                <div className="w-6 h-6 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Code2 className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-medium text-gray-300">AlgoBuddy</span>
              </div>
              <div className="text-gray-500 text-sm">
                © 2024 AlgoBuddy. Built with 💻 for developers.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;